// Bespoke building prefabs: military and resource buildings.
//
// Coordinate convention: origin at the building's base on the tile. Y up,
// X right, Z forward (toward camera). See buildings_extra.ts for the full
// group list; the shared `box()` helper and palette live in
// buildings_extra_shared.ts.

import { DARK_STONE, DARK_WOOD, STONE, WOOD, box } from './buildings_extra_shared.js';
import type { StructurePrefab } from './types.js';

// ── Military ────────────────────────────────────────────────────────────

export const assassinGuild: StructurePrefab = {
  name: "Assassin's Guild",
  blocks: [
    ...box(-1, 0, -1, 3, 1, 3, DARK_STONE),
    // Slender dark tower
    ...box(-1, 1, -1, 3, 7, 3, '#2a2a2a'),
    // Narrow windows (arrow slits)
    { x: 1, y: 3, z: 1, color: '#1a1a1a' },
    { x: 1, y: 5, z: 1, color: '#1a1a1a' },
    // Serrated crown
    { x: -2, y: 8, z: 0, color: '#1a1a1a' },
    { x: -2, y: 9, z: 0, color: '#1a1a1a' },
    { x: 0, y: 8, z: -2, color: '#1a1a1a' },
    { x: 0, y: 9, z: -2, color: '#1a1a1a' },
    { x: 2, y: 8, z: 0, color: '#1a1a1a' },
    { x: 2, y: 9, z: 0, color: '#1a1a1a' },
    { x: 0, y: 8, z: 2, color: '#1a1a1a' },
    { x: 0, y: 9, z: 2, color: '#1a1a1a' },
    // Crimson banner
    { x: 0, y: 6, z: 1, color: '#8b1a1a' },
    // Hidden door
    { x: 0, y: 1, z: 2, color: '#1a1a1a' },
    { x: 0, y: 2, z: 2, color: '#1a1a1a' },
  ],
};

export const siegeWorkshop: StructurePrefab = {
  name: 'Siege Workshop',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Open-air timber frame
    ...box(-2, 1, -2, 5, 1, 1, WOOD),
    ...box(-2, 1, 2, 5, 1, 1, WOOD),
    ...box(-2, 1, -2, 1, 1, 5, WOOD),
    ...box(2, 1, -2, 1, 1, 5, WOOD),
    ...box(-2, 4, -2, 5, 1, 1, WOOD),
    ...box(-2, 4, 2, 5, 1, 1, WOOD),
    ...box(-2, 4, -2, 1, 1, 5, WOOD),
    ...box(2, 4, -2, 1, 1, 5, WOOD),
    // Vertical posts
    { x: -2, y: 2, z: -2, color: WOOD },
    { x: 2, y: 2, z: -2, color: WOOD },
    { x: -2, y: 2, z: 2, color: WOOD },
    { x: 2, y: 2, z: 2, color: WOOD },
    { x: -2, y: 3, z: -2, color: WOOD },
    { x: 2, y: 3, z: -2, color: WOOD },
    { x: -2, y: 3, z: 2, color: WOOD },
    { x: 2, y: 3, z: 2, color: WOOD },
    // Catapult being built
    { x: 0, y: 1, z: 0, color: WOOD },
    { x: 1, y: 1, z: 0, color: WOOD },
    { x: -1, y: 1, z: 0, color: WOOD },
    { x: 0, y: 1, z: 1, color: WOOD },
    { x: 1, y: 1, z: 1, color: WOOD },
    { x: -1, y: 1, z: 1, color: WOOD },
    // Throwing arm (angled)
    { x: 0, y: 2, z: 0, color: WOOD },
    { x: 1, y: 3, z: 0, color: WOOD },
    { x: 2, y: 4, z: 0, color: WOOD },
    // Counterweight
    { x: -2, y: 2, z: 1, color: '#444444' },
  ],
};

export const slaveMarket: StructurePrefab = {
  name: 'Slave Market',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Wooden platform
    ...box(-2, 1, -2, 5, 1, 5, WOOD),
    // Auction block
    ...box(-1, 2, -1, 3, 1, 3, '#5a3a1a'),
    // Wooden cage with bars
    { x: -1, y: 3, z: -1, color: '#3a2010' },
    { x: 1, y: 3, z: -1, color: '#3a2010' },
    { x: -1, y: 4, z: -1, color: '#3a2010' },
    { x: 0, y: 4, z: -1, color: '#3a2010' },
    { x: 1, y: 4, z: -1, color: '#3a2010' },
    { x: -1, y: 5, z: -1, color: '#3a2010' },
    { x: 1, y: 5, z: -1, color: '#3a2010' },
    { x: -1, y: 3, z: 1, color: '#3a2010' },
    { x: 1, y: 3, z: 1, color: '#3a2010' },
    { x: -1, y: 4, z: 1, color: '#3a2010' },
    { x: 1, y: 4, z: 1, color: '#3a2010' },
    { x: -1, y: 5, z: 1, color: '#3a2010' },
    { x: 1, y: 5, z: 1, color: '#3a2010' },
    // Auctioneer's post
    { x: 0, y: 3, z: 1, color: WOOD },
    { x: 0, y: 4, z: 1, color: WOOD },
    // Whip-marked sign
    { x: 0, y: 5, z: 0, color: '#8b1a1a' },
  ],
};

// ─── Military / Resource (barracks, mine) ──────────────────

// Military training hall: stone block base, timber upper floor, red tile
// roof, flag pole on top, central doorway, narrow slit windows. ~8×8
// footprint, ~10 tall.
export const barracks: StructurePrefab = {
  name: 'Barracks',
  blocks: [
    // Stone foundation (8x8 footprint, centered)
    ...box(-3, 0, -3, 8, 1, 8, STONE),
    // Stone ground floor walls
    ...box(-3, 1, -3, 8, 3, 8, '#7d7a72'),
    // Timber upper floor walls
    ...box(-3, 4, -3, 8, 3, 8, '#6a4a2a'),
    // Doorway (front, center) — dark wood door
    ...box(0, 1, 4, 2, 3, 1, DARK_WOOD),
    // Narrow slit windows on the upper floor (front)
    { x: -2, y: 5, z: 4, color: '#1a1a1a' },
    { x: 3, y: 5, z: 4, color: '#1a1a1a' },
    // Narrow slit windows on the sides
    { x: -3, y: 5, z: 0, color: '#1a1a1a' },
    { x: 4, y: 5, z: 0, color: '#1a1a1a' },
    // Red tile roof — pitched (wide lower row, narrow upper row)
    ...box(-3, 7, -3, 8, 1, 8, '#8a2a2a'),
    ...box(-2, 8, -2, 6, 1, 6, '#7a1a1a'),
    ...box(-1, 9, -1, 4, 1, 4, '#6a0a0a'),
    // Ridge cap
    ...box(-1, 10, -1, 4, 1, 1, '#5a0000'),
    // Flag pole on the roof ridge
    { x: 1, y: 11, z: 0, color: '#3a3a3a' },
    { x: 1, y: 12, z: 0, color: '#3a3a3a' },
    { x: 1, y: 13, z: 0, color: '#3a3a3a' },
    // Crimson banner flag
    { x: 2, y: 12, z: 0, color: '#8b1a1a' },
    { x: 2, y: 13, z: 0, color: '#8b1a1a' },
    // Stone steps in front of the door
    { x: 0, y: 0, z: 5, color: STONE },
    { x: 1, y: 0, z: 5, color: STONE },
  ],
};

// Mine entrance: wooden support beams framing a dark opening, pile of rubble
// out front, mine cart tracks leading in, small lantern post. ~7 wide, ~6 tall.
export const mine: StructurePrefab = {
  name: 'Mine',
  blocks: [
    // Hillside / rubble base (stone)
    ...box(-3, 0, -2, 7, 1, 4, '#7d7a72'),
    // Dark opening (the adit) — 3 wide, 3 tall, set into the hillside
    ...box(-1, 1, -1, 3, 3, 1, '#1a1a1a'),
    // Wooden support frame around the opening (lintel + side posts)
    // Left post
    ...box(-2, 1, -1, 1, 4, 1, '#5a3a2a'),
    // Right post
    ...box(2, 1, -1, 1, 4, 1, '#5a3a2a'),
    // Top lintel beam
    ...box(-2, 5, -1, 5, 1, 1, '#5a3a2a'),
    // Cross-brace above the lintel (headframe)
    ...box(-2, 6, -1, 5, 1, 1, '#3a2010'),
    // Headframe tower (a small derrick over the entrance)
    { x: -3, y: 6, z: -1, color: '#5a3a2a' },
    { x: 3, y: 6, z: -1, color: '#5a3a2a' },
    { x: -3, y: 7, z: -1, color: '#3a2010' },
    { x: 3, y: 7, z: -1, color: '#3a2010' },
    { x: -3, y: 8, z: -1, color: '#3a2010' },
    { x: 3, y: 8, z: -1, color: '#3a2010' },
    // Pulley wheel at the top of the headframe
    { x: 0, y: 8, z: -1, color: '#444444' },
    // Pile of rubble out front
    { x: -3, y: 1, z: 1, color: '#9a9a92' },
    { x: -3, y: 1, z: 2, color: '#7d7a72' },
    { x: -2, y: 1, z: 2, color: '#9a9a92' },
    { x: 3, y: 1, z: 1, color: '#9a9a92' },
    { x: 3, y: 1, z: 2, color: '#7d7a72' },
    { x: 2, y: 1, z: 2, color: '#9a9a92' },
    // Mine cart tracks (dark line) leading into the opening
    { x: -1, y: 1, z: 1, color: '#2a2a2a' },
    { x: 0, y: 1, z: 1, color: '#2a2a2a' },
    { x: 1, y: 1, z: 1, color: '#2a2a2a' },
    { x: -1, y: 1, z: 2, color: '#2a2a2a' },
    { x: 0, y: 1, z: 2, color: '#2a2a2a' },
    { x: 1, y: 1, z: 2, color: '#2a2a2a' },
    // Mine cart on the tracks (just outside the entrance)
    { x: -1, y: 2, z: 2, color: '#5a3a2a' },
    { x: 0, y: 2, z: 2, color: '#5a3a2a' },
    { x: 1, y: 2, z: 2, color: '#5a3a2a' },
    { x: 0, y: 3, z: 2, color: '#3a2010' },
    // Lantern post to the left of the entrance
    { x: -3, y: 1, z: 0, color: '#3a2010' },
    { x: -3, y: 2, z: 0, color: '#3a2010' },
    { x: -3, y: 3, z: 0, color: '#3a2010' },
    // Glowing lantern head
    { x: -3, y: 4, z: 0, color: '#ffcc44', emissive: '#ff8800' },
  ],
};
